import React from 'react';
import useSEO from '../hooks/useSEO';
import { LegalDocument, LegalSection } from '../components/LegalDocument';

function PrivacyPolicy() {
  useSEO({
    title: 'Privacy Policy | Nightfall',
    description: 'How Nightfall collects, uses, and protects your personal information.',
    url: 'https://nightfallshop.com/privacy',
  });

  return (
    <LegalDocument
      title="Privacy Policy"
      subtitle="How we handle your data when you use Nightfall."
      lastUpdated="June 4, 2026"
    >
      <LegalSection title="Overview">
        <p>
          Nightfall ("we", "us", or "our") operates the Nightfall website and marketplace.
          This policy explains what information we collect, why we collect it, and the choices you have.
        </p>
      </LegalSection>

      <LegalSection title="Information we collect">
        <p>
          Account information such as username, email address, and password when you register.
        </p>
        <p>
          Purchase and library data including games you buy, download history, and transaction records processed through our payment partners.
        </p>
        <p>
          Usage data such as pages visited, device type, browser, and general analytics to improve the service.
        </p>
        <p>
          Communications you send to us, including support messages and bug reports.
        </p>
      </LegalSection>

      <LegalSection title="How we use information">
        <p>
          We use your information to operate the marketplace, process purchases, deliver downloads, secure accounts, and provide customer support.
        </p>
        <p>
          We may use aggregated or anonymized data to understand trends and improve Nightfall.
        </p>
        <p>
          We send service related emails when necessary, such as purchase confirmations or important account notices.
        </p>
      </LegalSection>

      <LegalSection title="Payment processing">
        <p>
          Payments are handled by third party processors such as Stripe.
          We do not store full payment card numbers on our servers.
          Payment providers receive the information required to complete transactions under their own privacy policies.
        </p>
      </LegalSection>

      <LegalSection title="Sharing of information">
        <p>
          We do not sell your personal information.
        </p>
        <p>
          We share data with service providers who help us run Nightfall, such as hosting, analytics, email delivery, and payment processing, only as needed to perform their services.
        </p>
        <p>
          We may disclose information if required by law, to protect our rights, or to prevent fraud and abuse.
        </p>
      </LegalSection>

      <LegalSection title="Cookies and local storage">
        <p>
          We use cookies and similar technologies for authentication, preferences, and analytics.
          You can control cookies through your browser settings, though some features may not work correctly if cookies are disabled.
        </p>
      </LegalSection>

      <LegalSection title="Data retention">
        <p>
          We retain account and purchase records for as long as your account is active or as needed to provide the service, comply with legal obligations, and resolve disputes.
        </p>
      </LegalSection>

      <LegalSection title="Your choices">
        <p>
          You may update account details from your account settings where available.
        </p>
        <p>
          You may request access, correction, or deletion of personal data by contacting us.
          We may need to retain certain records for legal or operational reasons.
        </p>
      </LegalSection>

      <LegalSection title="Security">
        <p>
          We use reasonable technical and organizational measures to protect your information.
          No online service can guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection title="Children">
        <p>
          Nightfall is intended for adults only.
          We do not knowingly collect personal information from anyone under 18.
        </p>
      </LegalSection>

      <LegalSection title="Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time.
          We will post the revised version on this page and update the date above.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          Questions about this policy can be sent through our support channels on the Nightfall website.
        </p>
      </LegalSection>
    </LegalDocument>
  );
}

export default PrivacyPolicy;
